import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronDown, Loader2, Mail, RotateCcw } from "lucide-react";
import { Reveal, Chapter, EASE } from "@/components/Reveal";

const inputClass =
  "mt-2 w-full border-b border-[#201e32]/25 bg-transparent px-0 py-3 text-sm outline-none transition-colors placeholder:text-[#6d687c]/60 focus:border-[#7650c7]";

const ContactForm = ({ onSent }) => {
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch("send-email.php", {
        method: "POST",
        body: new FormData(e.target),
        headers: { Accept: "application/json" },
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data && data.ok) {
        onSent();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <form
      data-testid="contact-form"
      onSubmit={submit}
      action="send-email.php"
      method="post"
      className="rounded-[1.5rem] bg-[#f3f0e8] p-6 text-[#201e32] sm:p-10"
    >
      <AnimatePresence>
        {status === "error" && (
          <motion.div
            data-testid="contact-error-banner"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mb-6 rounded-xl border border-[#c74545]/30 bg-[#c74545]/10 px-5 py-4 text-sm"
            role="alert"
          >
            <p className="font-semibold">Something went wrong — your note did not send.</p>
            <p className="mt-1 text-[#6d687c]">
              Try again, or email us directly at{" "}
              <a href="mailto:sales@elavatestudios.com" className="font-semibold text-[#7650c7] underline">
                sales@elavatestudios.com
              </a>
              .
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid gap-x-8 sm:grid-cols-2">
        <label className="block font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-[#201e32]/70">
          Your name
          <input
            required
            name="name"
            data-testid="contact-input-name"
            placeholder="What should we call you?"
            className={inputClass}
            maxLength={120}
          />
        </label>
        <label className="mt-7 block font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-[#201e32]/70 sm:mt-0">
          Email
          <input
            required
            type="email"
            name="email"
            data-testid="contact-input-email"
            placeholder="you@example.com"
            className={inputClass}
            maxLength={200}
          />
        </label>
      </div>

      <label className="mt-7 block font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-[#201e32]/70">
        What is this about?
        <span className="relative block">
          <select
            name="projectType"
            data-testid="contact-select-project-type"
            defaultValue="Something brand new"
            className={`${inputClass} cursor-pointer appearance-none pr-8`}
          >
            <option>Something brand new</option>
            <option>A refresh for an existing site</option>
            <option>Help with domain or hosting</option>
            <option>Not sure yet</option>
          </select>
          <ChevronDown
            size={15}
            aria-hidden
            className="pointer-events-none absolute bottom-4 right-1 text-[#6d687c]"
          />
        </span>
      </label>

      <label className="mt-7 block font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-[#201e32]/70">
        A little about it
        <textarea
          required
          name="message"
          data-testid="contact-input-message"
          rows={4}
          maxLength={5000}
          placeholder="The more context, the better — but bullet points are welcome."
          className={`${inputClass} resize-none`}
        />
      </label>

      <input
        type="text"
        name="company"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="pointer-events-none absolute left-[-9999px] h-0 w-0 opacity-0"
      />

      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          data-testid="contact-submit-button"
          disabled={status === "sending"}
          className="group flex w-fit items-center gap-3 rounded-full bg-[#7650c7] px-6 py-3.5 text-sm font-semibold text-[#f3f0e8] transition-[transform,opacity] duration-300 hover:-translate-y-1 disabled:translate-y-0 disabled:opacity-70"
        >
          {status === "sending" ? (
            <>
              Sending your note
              <Loader2 size={16} className="animate-spin" aria-hidden />
            </>
          ) : (
            <>
              Send enquiry
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </>
          )}
        </button>
        <a
          data-testid="contact-prefer-email-link"
          href="mailto:sales@elavatestudios.com"
          className="flex items-center gap-2 text-xs text-[#6d687c] transition-colors hover:text-[#7650c7]"
        >
          Prefer email? sales@elavatestudios.com
          <Mail size={13} aria-hidden />
        </a>
      </div>
    </form>
  );
};

export const Contact = () => {
  const [sent, setSent] = useState(false);

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#201e32] px-5 py-24 text-[#f3f0e8] sm:px-8 sm:py-32 lg:px-10"
    >
      <div className="mx-auto max-w-7xl">
        <Chapter n="06" label="Your turn" dark />

        <div className="mt-10 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-display text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl">
                Tell us
                <br />
                <span className="text-[#f4d35e]">the good stuff.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-7 max-w-[400px] text-base leading-relaxed text-white/60">
                A few words is plenty. We will come back with thoughtful questions, a clear
                route forward and no hard sell.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-10 flex flex-col gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
                <span>Reply within 24 hours</span>
                <span>No obligation, no hard sell</span>
                <span>Plain English, always</span>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-7">
            <AnimatePresence mode="wait">
              {sent ? (
                <motion.div
                  key="success"
                  data-testid="contact-success-panel"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="flex min-h-[450px] flex-col justify-center rounded-[1.5rem] bg-[#f3f0e8] p-7 text-[#201e32] sm:p-10"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#7abcb0]">
                    <ArrowUpRight size={22} className="text-[#201e32]" aria-hidden />
                  </span>
                  <p className="mt-8 font-display text-4xl font-bold leading-[0.95] tracking-[-0.04em] sm:text-5xl">
                    That is a<br />
                    great start.
                  </p>
                  <p className="mt-5 max-w-[380px] text-sm leading-relaxed text-[#6d687c]">
                    Your note is safely with us. We will read it properly and get back to you
                    within 24 hours — and a quick confirmation is on its way to your inbox too.
                  </p>
                  <button
                    type="button"
                    data-testid="contact-send-another-button"
                    onClick={() => setSent(false)}
                    className="mt-8 flex w-fit items-center gap-2 text-sm font-semibold text-[#7650c7] transition-colors hover:text-[#201e32]"
                  >
                    Send another note
                    <RotateCcw size={15} aria-hidden />
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.5, ease: EASE }}
                >
                  <ContactForm onSent={() => setSent(true)} />
                </motion.div>
              )}
            </AnimatePresence>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
